import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  oneTimePlansService,
  OneTimePlanCreateData,
  OneTimePlanUpdateData,
  OneTimePlanFilters,
  OneTimePlanSortOptions
} from '@/services/billing/one-time-plans.service';
import { toast } from 'sonner';
import { TranslatedOneTimePlan, TranslationParams } from '@/types/billing';

// Helper function for error handling
function getErrorMessage(error: unknown, defaultMessage: string): string {
  if (error && 
      typeof error === 'object' && 
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object') {
    
    // Check for JSON:API error format
    const data = error.response.data as Record<string, unknown>;
    if ('errors' in data && Array.isArray(data.errors) && data.errors.length > 0) {
      const firstError = data.errors[0] as Record<string, unknown>;
      if ('detail' in firstError && typeof firstError.detail === 'string') {
        return firstError.detail;
      }
    }
    
    // Fallback to message field
    if ('message' in data && typeof data.message === 'string') {
      return data.message;
    }
  }
  return defaultMessage;
}

// Query Keys
export const oneTimePlanKeys = {
  all: ['one-time-plans'] as const,
  lists: () => [...oneTimePlanKeys.all, 'list'] as const,
  list: (productId: string, filters?: OneTimePlanFilters, sort?: OneTimePlanSortOptions, translationParams?: TranslationParams) => 
    [...oneTimePlanKeys.lists(), productId, filters, sort, translationParams] as const,
  details: () => [...oneTimePlanKeys.all, 'detail'] as const,
  detail: (productId: string, planId: string, translationParams?: TranslationParams) => 
    [...oneTimePlanKeys.details(), productId, planId, translationParams] as const,
};

/**
 * Hook to fetch all one-time plans for a product
 */
export function useOneTimePlans(
  productId: string,
  filters?: OneTimePlanFilters,
  sort?: OneTimePlanSortOptions,
  translationParams?: TranslationParams
) {
  return useQuery<TranslatedOneTimePlan[]>({
    queryKey: oneTimePlanKeys.list(productId, filters, sort, translationParams),
    queryFn: () => oneTimePlansService.getOneTimePlans(productId, filters, sort, translationParams),
    enabled: !!productId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch a single one-time plan
 */
export function useOneTimePlan(
  productId: string, 
  planId: string,
  translationParams?: TranslationParams
) {
  return useQuery<TranslatedOneTimePlan>({
    queryKey: oneTimePlanKeys.detail(productId, planId, translationParams),
    queryFn: () => oneTimePlansService.getOneTimePlan(productId, planId, translationParams),
    enabled: !!productId && !!planId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to create a new one-time plan
 */
export function useCreateOneTimePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: OneTimePlanCreateData }) =>
      oneTimePlansService.createOneTimePlan(productId, data),
    onSuccess: (newPlan, { productId }) => {
      // Set the detail cache for the new plan
      queryClient.setQueryData(
        oneTimePlanKeys.detail(productId, newPlan.id),
        newPlan
      );
      
      // Invalidate all list queries for this product to trigger refetch
      queryClient.invalidateQueries({ 
        queryKey: oneTimePlanKeys.lists(),
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.length >= 3 && queryKey[2] === productId;
        }
      });
      
      toast.success('One-time plan created successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to create one-time plan'));
    },
  });
}

/**
 * Hook to update a one-time plan (full update)
 */
export function useUpdateOneTimePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      productId, 
      planId, 
      data 
    }: { 
      productId: string; 
      planId: string; 
      data: OneTimePlanUpdateData 
    }) =>
      oneTimePlansService.updateOneTimePlan(productId, planId, data),
    onSuccess: (updatedPlan, { productId, planId }) => {
      // Update the detail cache
      queryClient.setQueryData(
        oneTimePlanKeys.detail(productId, planId),
        updatedPlan
      );
      
      // Invalidate all list queries for this product to trigger refetch
      queryClient.invalidateQueries({ 
        queryKey: oneTimePlanKeys.lists(),
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.length >= 3 && queryKey[2] === productId;
        }
      });
      
      toast.success('One-time plan updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update one-time plan'));
    },
  });
}

/**
 * Hook to partially update a one-time plan
 */
export function usePatchOneTimePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      productId, 
      planId, 
      data 
    }: { 
      productId: string; 
      planId: string; 
      data: Partial<OneTimePlanUpdateData> 
    }) =>
      oneTimePlansService.patchOneTimePlan(productId, planId, data),
    onSuccess: (updatedPlan, { productId, planId }) => {
      // Update the detail cache
      queryClient.setQueryData(
        oneTimePlanKeys.detail(productId, planId),
        updatedPlan
      );
      
      // Invalidate all list queries for this product to trigger refetch
      queryClient.invalidateQueries({ 
        queryKey: oneTimePlanKeys.lists(),
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.length >= 3 && queryKey[2] === productId;
        }
      });
      
      toast.success('One-time plan updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update one-time plan'));
    },
  });
}

/**
 * Hook to delete a one-time plan
 */
export function useDeleteOneTimePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, planId }: { productId: string; planId: string }) =>
      oneTimePlansService.deleteOneTimePlan(productId, planId),
    onSuccess: (_, { productId, planId }) => {
      // Remove detail cache
      queryClient.removeQueries({ queryKey: oneTimePlanKeys.detail(productId, planId) });
      
      // Invalidate all list queries for this product to trigger refetch
      queryClient.invalidateQueries({ 
        queryKey: oneTimePlanKeys.lists(),
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.length >= 3 && queryKey[2] === productId;
        }
      });
      
      toast.success('One-time plan deleted successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to delete one-time plan. Please archive it instead if there are active purchases.'));
    },
  });
}

/**
 * Hook to archive a one-time plan
 */
export function useArchiveOneTimePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, planId }: { productId: string; planId: string }) =>
      oneTimePlansService.archiveOneTimePlan(productId, planId),
    onSuccess: (updatedPlan, { productId, planId }) => {
      // Update the detail cache
      queryClient.setQueryData(
        oneTimePlanKeys.detail(productId, planId),
        updatedPlan
      );
      
      // Invalidate all list queries for this product to trigger refetch
      queryClient.invalidateQueries({ 
        queryKey: oneTimePlanKeys.lists(),
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.length >= 3 && queryKey[2] === productId;
        }
      });
      
      toast.success('One-time plan archived successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to archive one-time plan'));
    },
  });
}

/**
 * Prefetch a one-time plan for hover/preview scenarios
 */
export function usePrefetchOneTimePlan() {
  const queryClient = useQueryClient();

  return (productId: string, planId: string) => {
    queryClient.prefetchQuery({
      queryKey: oneTimePlanKeys.detail(productId, planId),
      queryFn: () => oneTimePlansService.getOneTimePlan(productId, planId),
      staleTime: 5 * 60 * 1000,
    });
  };
}

/**
 * Hook to calculate monthly equivalent pricing
 */
export function useOneTimePlanCalculation() {
  return {
    calculateMonthlyEquivalent: (price: number, durationMonths: number | 'unlimited') => 
      oneTimePlansService.calculateMonthlyEquivalent(price, durationMonths),
    formatAccessDuration: (duration: number | 'unlimited') => 
      oneTimePlansService.formatAccessDuration(duration),
    validateAccessDuration: (duration: number | 'unlimited') => 
      oneTimePlansService.validateAccessDuration(duration)
  };
}